import {
  ANALYTICS_EVENT_NAMES,
  type AnalyticsRange,
  type AppDataAvailability,
  type StaffAnalyticsAccess,
} from "@lafayette/schemas";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { EmptyState } from "../../ui/empty-state";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import { Notice } from "../../ui/notice";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../ui/sheet";
import { firstNameOf } from "../event-sentences";
import { countText } from "../format";
import { type CustomerView, isBadRequest, isRefusal } from "../queries";
import { EventDetailPanel } from "./EventDetailPanel";
import { EventTable } from "./EventTable";
import { useEventsAddress } from "./events-address";
import { AREA_LABELS, AREA_ORDER, matchesSearch, windowText } from "./events-model";
import { useEventCount, useEventPages } from "./events-queries";
import { VisitSheet } from "./VisitSheet";

// The Events tab (Paper "Analytics · Events (raw)"): the raw events of one customer for analysts, read back on the server. Filters by area, a search
// of what is on screen, "Add filter" (one event name, or one visit), "Load older events", and the detail panel of the open event (a sheet on a
// narrow screen). The page says in words that message text is never shown. An opted-out customer shows no events and says why.

// The table of the kit becomes a list of cards below 720 px of its own width. Beside a 388 px panel it keeps its columns only from this width up
// (the shell, the page padding and the card around it take the rest). Below it the panel is a sheet, so the table always has its columns.
const WIDE = "(min-width: 1560px)";

/** `true` from 1560 px up (the panel sits beside the table); below, the panel is a sheet. Without `matchMedia` (a test) it is wide. */
function useIsWide(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window.matchMedia === "function" ? window.matchMedia(WIDE).matches : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(WIDE);
    const listener = () => setWide(query.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);
  return wide;
}

/** One filter chip of the mock-up: a pill, 30 px high. The pressed one is filled; the state is also in `aria-pressed`. */
function Chip({
  active,
  onSelect,
  children,
}: Readonly<{ active: boolean; onSelect: () => void; children: ReactNode }>) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "default" : "outline"}
      aria-pressed={active}
      onClick={onSelect}
      className="h-[30px] rounded-full px-3 text-[13px] compact:h-[30px]"
    >
      {children}
    </Button>
  );
}

function AvailabilityNotice({
  availability,
  name,
  onRetry,
}: Readonly<{
  availability: Exclude<AppDataAvailability, "AVAILABLE">;
  name: string;
  onRetry: () => void;
}>) {
  if (availability === "VENDOR_UNAVAILABLE") {
    return (
      <AsyncState
        kind="error"
        title="App events could not be read right now"
        message="The records on the Overview are not affected. Try again in a moment."
        onRetry={onRetry}
      />
    );
  }
  return (
    <Notice tone={availability === "OPTED_OUT" ? "info" : "neutral"} role="note">
      {availability === "OPTED_OUT"
        ? `${name} chose not to share app analytics. No app events are shown. The records are on the Overview.`
        : "App events are not available yet. The records are on the Overview."}
    </Notice>
  );
}

function AddFilter({
  onEvent,
  onVisit,
  openVisitId,
}: Readonly<{
  onEvent: (name: string) => void;
  onVisit: (id: string) => void;
  openVisitId: string | null;
}>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="secondary" className="rounded-[10px]">
          <Icon name="plus-sign" size="sm" />
          Add filter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {openVisitId === null ? null : (
          <DropdownMenuItem onSelect={() => onVisit(openVisitId)}>
            Only the open visit
          </DropdownMenuItem>
        )}
        <DropdownMenuLabel>One event name</DropdownMenuLabel>
        {ANALYTICS_EVENT_NAMES.map((name) => (
          <DropdownMenuItem
            key={name}
            className="font-mono text-[13px]"
            onSelect={() => onEvent(name)}
          >
            {name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Address = ReturnType<typeof useEventsAddress>;
type Pages = ReturnType<typeof useEventPages>;

function EventsToolbar({
  address,
  openVisit,
}: Readonly<{ address: Address; openVisit: string | null }>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        type="search"
        value={address.search}
        onChange={(change) => address.set({ q: change.target.value.slice(0, 64) })}
        placeholder="Find an event or property"
        aria-label="Find an event or property on this page"
        className="h-9 w-56 rounded-[10px] text-[13px] compact:h-8"
      />
      <AddFilter
        onEvent={(event) => address.set({ event })}
        onVisit={(visit) => address.set({ only: visit })}
        openVisitId={openVisit}
      />
    </div>
  );
}

function AreaChips({ address, total }: Readonly<{ address: Address; total: number | null }>) {
  const filtered = address.area !== null || address.event !== null || address.onlyVisitId !== null;
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
      <legend className="sr-only">Filter by area</legend>
      <Chip active={address.area === null} onSelect={() => address.set({ area: null })}>
        {total === null || filtered ? "All" : `All · ${countText(total)}`}
      </Chip>
      {AREA_ORDER.map((area) => (
        <Chip key={area} active={address.area === area} onSelect={() => address.set({ area })}>
          {AREA_LABELS[area]}
        </Chip>
      ))}
    </fieldset>
  );
}

function RemovableFilter({
  children,
  onRemove,
  mono = false,
}: Readonly<{ children: ReactNode; onRemove: () => void; mono?: boolean }>) {
  return (
    <Button
      type="button"
      variant="soft"
      size="sm"
      className={mono ? "font-mono text-[13px]" : undefined}
      onClick={onRemove}
    >
      {children}
      <Icon name="cancel-01" size="sm" />
      <span className="sr-only">Remove this filter</span>
    </Button>
  );
}

function ActiveFilters({ address, tz }: Readonly<{ address: Address; tz: string }>) {
  if (address.event === null && address.onlyVisitId === null && address.window === null)
    return null;
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0">
      <legend className="sr-only">Active filters</legend>
      {address.event === null ? null : (
        <RemovableFilter mono onRemove={() => address.set({ event: null })}>
          {address.event}
        </RemovableFilter>
      )}
      {address.onlyVisitId === null ? null : (
        <RemovableFilter onRemove={() => address.set({ only: null })}>
          One visit only
        </RemovableFilter>
      )}
      {address.window === null ? null : (
        <RemovableFilter onRemove={() => address.set({ from: null, to: null })}>
          Window: {windowText(address.window, tz)}
        </RemovableFilter>
      )}
    </fieldset>
  );
}

function EventsFooter({
  shown,
  total,
  pages,
}: Readonly<{ shown: number; total: number | null; pages: Pages }>) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="m-0 text-xs leading-4 text-muted-foreground" aria-live="polite">
        Showing {countText(shown)}
        {total === null ? "" : ` of ${countText(total)}`}. Message text is never shown.
      </p>
      {pages.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-[10px]"
          loading={pages.isFetchingNextPage}
          onClick={() => pages.fetchNextPage()}
        >
          Load older events
        </Button>
      ) : null}
    </div>
  );
}

function NoEvents({
  name,
  searching,
  filtered,
}: Readonly<{ name: string; searching: boolean; filtered: boolean }>) {
  return (
    <EmptyState
      icon={<Icon name="note-01" size="lg" />}
      title={searching ? "No event on this page matches" : "No events here"}
      description={
        searching
          ? "The search looks at the events that are loaded. Load older events, or clear the search."
          : `${name} has no events in this range${filtered ? " with these filters" : ""}. Try a longer range.`
      }
    />
  );
}

/** The panel of the open event beside the table, or in a sheet on a narrow screen. Nothing when no event is open. */
function PanelSlot({
  address,
  wide,
  view,
  customerId,
  access,
}: Readonly<{
  address: Address;
  wide: boolean;
  view: CustomerView;
  customerId: string;
  access: StaffAnalyticsAccess;
}>) {
  const { openId } = address;
  if (openId === null) return null;
  const onClose = () => {
    // The inline panel is not a dialog: nothing returns the focus when it closes. The row button of the open event stays in the table (the
    // columns do not rebuild), so the focus goes back to it. The sheet returns its own focus (`SheetContent`).
    const opener = wide ? document.querySelector<HTMLElement>('button[aria-current="true"]') : null;
    address.set({ open: null });
    opener?.focus();
  };
  const panel = (
    <EventDetailPanel
      showClose={wide}
      view={view}
      customerId={customerId}
      eventUuid={openId}
      access={access}
      onClose={onClose}
      onShowOnly={(event) => address.set({ event, open: null })}
      onShowVisit={(visit) => address.set({ only: visit })}
    />
  );
  if (wide) return <div className="sticky top-4">{panel}</div>;
  return (
    <Sheet open onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent
        side="right"
        closeLabel="Close the event"
        className="w-[min(26rem,100vw)] overflow-y-auto bg-card p-3 text-foreground"
      >
        <SheetTitle className="sr-only">Event</SheetTitle>
        <SheetDescription className="sr-only">
          The event, its properties and its visit step by step.
        </SheetDescription>
        {panel}
      </SheetContent>
    </Sheet>
  );
}

/** The sheet of one visit, when the address names one (`?visit=<visit id>`). Nothing otherwise. */
function VisitSheetSlot({
  address,
  view,
  customerId,
  access,
}: Readonly<{
  address: Address;
  view: CustomerView;
  customerId: string;
  access: StaffAnalyticsAccess;
}>) {
  const { visitId } = address;
  if (visitId === null) return null;
  return (
    <VisitSheet
      view={view}
      customerId={customerId}
      visitId={visitId}
      access={access}
      onClose={() => address.set({ visit: null })}
      onShowEvents={() => address.set({ visit: null, only: visitId })}
    />
  );
}

/** The rows on screen (the loaded pages, then the search) and the total that the tab may show beside them. */
function useEventRows(pages: Pages, address: Address, count: number | null) {
  const all = useMemo(() => pages.data?.pages.flatMap((page) => page.rows) ?? [], [pages.data]);
  const rows = useMemo(
    () => all.filter((row) => matchesSearch(row, address.search)),
    [all, address.search],
  );
  const filtered = address.area !== null || address.event !== null || address.onlyVisitId !== null;
  const total = filtered || address.search !== "" ? null : count;
  const openVisit = all.find((row) => row.uuid === address.openId)?.visitId ?? null;
  return { rows, filtered, total, openVisit };
}

/** The answer that replaces the table: a refusal, a failed read, or why no app data is shown. `null`: show the table. */
function gateOf(pages: Pages, name: string, address: Address): ReactNode {
  if (isRefusal(pages.error)) {
    return (
      <AsyncState
        kind="error"
        title="This customer is not available to you"
        message="You may open only the customers you are allowed to see. If you think this is a mistake, ask a supervisor."
      />
    );
  }
  if (pages.isError && address.window !== null && isBadRequest(pages.error)) {
    return (
      <AsyncState
        kind="error"
        title="This time window is not valid"
        message="The window in the address is outside the range, or shorter than one hour."
        accessory={
          <Button
            type="button"
            variant="outline"
            onClick={() => address.set({ from: null, to: null })}
          >
            Show the whole range
          </Button>
        }
      />
    );
  }
  if (pages.isError) {
    return (
      <AsyncState
        kind="error"
        title="The events could not be loaded"
        message="Nothing was changed. Try again in a moment."
        onRetry={() => pages.refetch()}
      />
    );
  }
  const availability = pages.data?.pages[0]?.availability ?? "AVAILABLE";
  if (availability === "AVAILABLE") return null;
  return (
    <AvailabilityNotice availability={availability} name={name} onRetry={() => pages.refetch()} />
  );
}

export function EventsTab({
  view,
  customerId,
  range,
  access,
}: Readonly<{
  view: CustomerView;
  customerId: string;
  range: AnalyticsRange;
  access: StaffAnalyticsAccess;
}>) {
  const address = useEventsAddress();
  const wide = useIsWide();
  const pages = useEventPages(view, customerId, range, {
    area: address.area ?? undefined,
    event: address.event ?? undefined,
    visitId: address.onlyVisitId,
    window: address.window,
  });
  const count = useEventCount(view, customerId, range, true, address.window);
  const name = firstNameOf(access.displayName);
  const { rows, filtered, total, openVisit } = useEventRows(
    pages,
    address,
    count.data?.count ?? null,
  );
  const gate = gateOf(pages, name, address);
  if (gate !== null) return gate;
  return (
    <div
      className={cn(
        "grid items-start gap-4",
        wide && address.openId !== null ? "grid-cols-[minmax(0,1fr)_388px]" : "grid-cols-1",
      )}
    >
      <Card className="rounded-2xl">
        <CardHeader className="grid-cols-1 gap-3 pb-3 sm:flex sm:flex-wrap sm:items-start sm:justify-between">
          <div className="grid gap-0.5">
            <CardTitle as="h2" className="text-xl leading-6">
              All events
            </CardTitle>
            <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
              Newest first · click a row to open it
            </p>
          </div>
          <EventsToolbar address={address} openVisit={openVisit} />
        </CardHeader>
        <CardContent className="gap-3">
          <AreaChips address={address} total={count.data?.count ?? null} />
          <ActiveFilters address={address} tz={access.window.tz} />
          <EventTable
            rows={rows}
            openUuid={address.openId}
            tz={access.window.tz}
            now={access.window.to}
            onOpen={(uuid) => address.set({ open: uuid })}
            isLoading={pages.isPending}
            emptyState={
              <NoEvents name={name} searching={address.search !== ""} filtered={filtered} />
            }
          />
          <EventsFooter shown={rows.length} total={total} pages={pages} />
        </CardContent>
      </Card>
      <PanelSlot
        address={address}
        wide={wide}
        view={view}
        customerId={customerId}
        access={access}
      />
      <VisitSheetSlot address={address} view={view} customerId={customerId} access={access} />
    </div>
  );
}
